import { FlexColumn, FlexRow, LabeledInput, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { updateFleetAsset } from '../../../services/fleetService';
import { apiParamsState } from '../../../storage/app';
import { FleetAsset, FleetAssetRequest } from '../../../types/fleet';
import { getAssetTitle } from '../../../utils/assets';
import { isBrand, isSubBrand, isVersion } from '../../../utils/currentStates';
import { preventDotAndComma, preventMinus } from '../../../utils/formUtilis';
import { BrandSelect } from '../../shared/BrandSelect';
import { SubBrandSelect } from '../../shared/SubBrandSelect';
import VehicleColorSelect from '../../shared/VehicleColorSelect';
import VehicleModelSelect from '../../shared/VehicleModelSelect';
import { VersionSelect } from '../../shared/VersionSelect';

type Props = {
  asset: FleetAsset;
  onCancel: () => void;
  afterSave: () => void;
};

export function FleetAssetForm({ asset, onCancel, afterSave }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const assetLabel = `${getAssetTitle(asset)} (${asset.label})`;
  const apiParams = useRecoilValue(apiParamsState);
  const [editingAsset, setEditingAsset] = useState<FleetAssetRequest>({
    clientId: apiParams?.clientId ?? '',
    subClientId: apiParams?.subClientId ?? '',
    brandId: isBrand(asset.vehicle?.brand) ? asset.vehicle.brand._id : asset.vehicle?.brand || '',
    color: asset.vehicle?.color ?? '',
    year: asset.vehicle?.year ?? new Date().getFullYear(),
    licensePlate: asset.vehicle?.licensePlate,
    subBrandId: isSubBrand(asset.vehicle?.subBrand) ? asset.vehicle.subBrand._id : asset.vehicle?.subBrand,
    versionId: isVersion(asset.vehicle?.version) ? asset.vehicle.version._id : asset.vehicle?.version,
    label: asset.label ?? '',
    marketValue: asset.vehicle?.marketValue,
    odometer: asset.odometerState?.userInputOdometer,
  });
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const brandObject = isBrand(asset.vehicle?.brand) ? asset.vehicle.brand : undefined;
  const subBrandObject = isSubBrand(asset.vehicle?.subBrand) ? asset.vehicle.subBrand : undefined;
  const versionObject = isVersion(asset.vehicle?.version) ? asset.vehicle.version : undefined;

  useEffect(() => {
    if (asset && apiParams) {
      setEditingAsset({
        clientId: asset.client?._id ?? apiParams.clientId,
        subClientId: asset.subClient?._id ?? apiParams.subClientId,
        brandId: isBrand(asset.vehicle?.brand) ? asset.vehicle.brand._id : asset.vehicle?.brand || '',
        color: asset.vehicle?.color ?? '',
        year: asset.vehicle?.year ?? new Date().getFullYear(),
        licensePlate: asset.vehicle?.licensePlate,
        subBrandId: isSubBrand(asset.vehicle?.subBrand) ? asset.vehicle.subBrand._id : asset.vehicle?.subBrand,
        versionId: isVersion(asset.vehicle?.version) ? asset.vehicle.version._id : asset.vehicle?.version,
        label: asset.label ?? '',
        marketValue: asset.vehicle?.marketValue,
        odometer: asset.odometerState?.userInputOdometer,
      });
    }
  }, [asset, apiParams]);

  const canSubmit = useMemo(() => {
    return !!editingAsset.color && !!editingAsset.year && !!editingAsset.brandId;
  }, [editingAsset]);

  const onInputChange = (value: string | undefined | null | {}, field: keyof FleetAssetRequest) => {
    setEditingAsset((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    startLoading();
    const { id, error } = await updateFleetAsset(asset._id, editingAsset);
    if (!error && id) {
      afterSave();
      showPositiveFeedback(FEEDBACK.edited(FEEDBACK_PREFIXES.asset, assetLabel));
    } else {
      showFailFeedback(FEEDBACK.failedEdition(FEEDBACK_PREFIXES.maintenanceRule, assetLabel));
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'end',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        })}
      >
        <LabeledInput
          label="Etiqueta:"
          value={editingAsset.label}
          onChange={(label) => onInputChange(label, 'label')}
          required
        />
        <LabeledInput
          label="Vin:"
          value={asset.vehicle?.vin}
          caption="Este campo no se puede editar"
          readonly
        />
        <LabeledInput
          label="Placa:"
          value={editingAsset.licensePlate}
          onChange={(plate) => onInputChange(plate, 'licensePlate')}
          required
        />
        <BrandSelect
          brand={brandObject}
          selectedBrandId={editingAsset.brandId}
          onChangeBrandId={(brandId) => {
            onInputChange(brandId, 'brandId');
          }}
          required
        />
        <SubBrandSelect
          subBrand={subBrandObject}
          selectedBrandId={editingAsset.brandId}
          selectedSubBrandId={editingAsset.subBrandId}
          onChangeSubBrand={(subBrandId) => {
            onInputChange(subBrandId, 'subBrandId');
          }}
        />
        <VersionSelect
          version={versionObject}
          selectedVersionId={editingAsset.versionId}
          selectedBrandId={editingAsset.brandId}
          selectedSubBrandId={editingAsset.subBrandId}
          onChangeVersion={(versionId) => {
            onInputChange(versionId, 'versionId');
          }}
          disabled={!editingAsset.subBrandId}
        />
        <VehicleModelSelect
          model={editingAsset.year}
          onChangeYear={(year) => onInputChange(year, 'year')}
          required
        />
        <VehicleColorSelect
          label="Color:"
          selectedColor={editingAsset.color}
          onChangeColor={(color) => {
            onInputChange(color, 'color');
          }}
          required
        />
        <LabeledInput
          label="Valor comercial:"
          value={editingAsset.marketValue ?? 0}
          onChange={(marketValue) => onInputChange((+marketValue).toString(), 'marketValue')}
          type="number"
          onKeyDown={(event) => {
            preventMinus(event);
          }}
          startEnhancer="$"
        />
        <LabeledInput
          label="Odómetro:"
          value={editingAsset.odometer ?? 0}
          onChange={(odometer) => onInputChange((+odometer).toString(), 'odometer')}
          type="number"
          onKeyDown={(event) => {
            preventMinus(event);
            preventDotAndComma(event);
          }}
          endEnhancer="km"
        />
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {asset?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
